import {
  getAncestorWithTag,
  getShadowSelection,
  resolveInnerNodeByPath,
} from '@packages/systems/code-libraries/inner-node-utils';
import {getNativeNodeByNativeIdPath} from '@packages/systems/core/utils/NativeIdUtils';
import {cssToPropertyMap} from '@packages/systems/core/utils/CssStringUtils';
import {CSS_PROPERTY_DETAILS} from '@packages/systems/style/properties';
import {getInferredStyles} from '@packages/utilities/element-style-inference';
import {
  getTextOverrideKey,
  getSourceStyleOverrideKey,
  getSourceStyleOverrideKeyBase,
  getStyleOverrideKey,
  vccStyleOverridesUpdated,
  type TextDraftAttributes,
  type TextDraftContent,
  type VCCStyleOverridesMap,
  type VCCStyleSourceIdentity,
  type VCCTextOverridesMap,
  type VCCTextEditingTarget,
} from './actionCreators';
import {
  readDraftSourceIdentity,
  resolveStoredDraftSourceIdentity,
} from '../crossStateStyleCascade/draftSourceIdentity';
import type {VccTextFormat} from './toggleVccTextFormat';
import {vccStyleOverrideHasChanges} from './vccStyleOverrideUtils';
import {snapshotVccInstanceStyleBaseline} from './vccInstanceStyleBaselines';
import {ensureVccInstancePaintedProps} from './vccInstancePaintedProps';

/**
 * The longhand CSS properties the VCC style panel supports, derived from
 * CSS_PROPERTY_DETAILS (single source of truth). Excludes the `expandedCss`
 * section (custom/advanced properties) and shorthands (those with a
 * `computed` array). Used as the backfill list when inferring an element's
 * baseline styles. Re-exported by the panel's `styleReader`.
 */
const STYLE_PANEL_PROPERTIES: string[] = [];

(function buildPropertyList() {
  const details = CSS_PROPERTY_DETAILS as Record<
    string,
    {section?: string; computed?: string[]}
  >;

  for (const [prop, detail] of Object.entries(details)) {
    if (!detail.section || detail.section === 'expandedCss') continue;
    if (detail.computed) continue;
    STYLE_PANEL_PROPERTIES.push(prop);
  }
})();

export {STYLE_PANEL_PROPERTIES};

/**
 * Sets (or removes, with `null`) one property in a styleLess declaration
 * string, preserving the others. The serialization format is what the VCC
 * style panel stores in draft entries. Re-exported by the panel's
 * `styleLessParser`.
 */
export function updateStyleLessProperty(
  styleLess: string,
  cssProp: string,
  cssValue: string | null
): string {
  const map = cssToPropertyMap(styleLess || '');

  if (cssValue === null) {
    map[cssProp] = undefined;
  } else {
    map[cssProp] = cssValue;
  }

  const entries = Object.entries(map).filter(([, val]) => val !== undefined);
  return entries
    .map(([prop, val]) => `${prop}: ${val}`)
    .join('; ')
    .concat(entries.length > 0 ? ';' : '');
}

export type VccStyleDraftContext = {
  styleOverrides: VCCStyleOverridesMap;
  textOverrides?: VCCTextOverridesMap;
  breakpointId: string;
  dispatch: (action: ReturnType<typeof vccStyleOverridesUpdated>) => void;
};

// Each CSS-routed format maps to the property the style panel's typography
// section reads and writes, so both surfaces stay one state (EXT-1854).
// `tags` are the inline-markup sources that keep a toggle on the
// execCommand wrap/unwrap path instead.
const FORMAT_STYLES: Partial<
  Record<
    VccTextFormat,
    {
      attribute: keyof TextDraftAttributes;
      prop: string;
      tags: string[];
      isComputedOn: (computed: CSSStyleDeclaration) => boolean;
      isValueOn: (value: string) => boolean;
      onValue: string;
      offValue: string;
    }
  >
> = {
  italic: {
    attribute: 'italic',
    prop: 'font-style',
    tags: ['I', 'EM'],
    isComputedOn: (computed) => computed.fontStyle === 'italic',
    isValueOn: (value) => value === 'italic',
    onValue: 'italic',
    offValue: 'normal',
  },
  bold: {
    attribute: 'bold',
    prop: 'font-weight',
    tags: ['B', 'STRONG'],
    isComputedOn: (computed) => parseInt(computed.fontWeight, 10) >= 600,
    isValueOn: (value) => parseInt(value, 10) >= 600,
    onValue: '700',
    offValue: '400',
  },
  underline: {
    attribute: 'underline',
    prop: 'text-decoration',
    tags: ['U'],
    // textDecorationLine is empty in engines that only expose the
    // shorthand (jsdom), so read both.
    isComputedOn: (computed) =>
      (computed.textDecorationLine || computed.textDecoration || '').includes(
        'underline'
      ),
    isValueOn: (value) => value.includes('underline'),
    onValue: 'underline',
    offValue: 'none',
  },
};

/**
 * Computed-style state for the CSS-routed formats. Pairs with
 * `getWholeElementFormatState`'s markup view for the toolbar's idle state,
 * so a button reads active exactly when a press would toggle the format off.
 */
export function getCssTextFormatState(
  element: HTMLElement
): Record<'bold' | 'italic' | 'underline', boolean> {
  const win = element.ownerDocument.defaultView;
  const computed = win?.getComputedStyle(element);
  return {
    bold: Boolean(computed && FORMAT_STYLES.bold?.isComputedOn(computed)),
    italic: Boolean(computed && FORMAT_STYLES.italic?.isComputedOn(computed)),
    underline: Boolean(
      computed && FORMAT_STYLES.underline?.isComputedOn(computed)
    ),
  };
}

type ResolvedTarget = {
  element: HTMLElement;
  selection: Selection;
  range: Range;
};

// Resolves the edited element and requires a selection spanning its entire
// text — a sub-range can't be expressed as element CSS, so callers fall back
// to the markup path when this returns null.
function resolveFullTextSelection(
  editing: VCCTextEditingTarget
): ResolvedTarget | null {
  const host = getNativeNodeByNativeIdPath(editing.nativeIdPath);
  const shadowRoot = host?.shadowRoot;
  if (!shadowRoot) return null;
  const element = resolveInnerNodeByPath(shadowRoot, editing.nodeId);
  if (!element) return null;

  const selection = getShadowSelection(shadowRoot);
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (range.collapsed || !element.contains(range.commonAncestorContainer)) {
    return null;
  }

  const selectedText = range.toString().trim();
  const elementText = (element.textContent ?? '').trim();
  if (selectedText.length === 0 || selectedText !== elementText) return null;

  return {element, selection, range};
}

// The identity this element's draft is read and written under, or `undefined`
// for the legacy nodeId + scenario path.
function resolveDraftIdentity(
  element: HTMLElement,
  context: VccStyleDraftContext
): VCCStyleSourceIdentity | undefined {
  const live = readDraftSourceIdentity(element);
  if (!live) return undefined;
  return resolveStoredDraftSourceIdentity(
    element,
    live,
    context.styleOverrides
  );
}

function getStoredEntry(
  editing: VCCTextEditingTarget,
  context: VccStyleDraftContext,
  identity: VCCStyleSourceIdentity | undefined
): VCCStyleOverridesMap[string] | undefined {
  // A stamped element's draft is keyed by source identity, not nodeId + scenario.
  const key = identity
    ? getSourceStyleOverrideKey(
        identity,
        editing.variant?.id,
        context.breakpointId,
        null
      )
    : getStyleOverrideKey(
        editing.nodeId,
        editing.variant?.id,
        context.breakpointId,
        null,
        editing.componentState?.id
      );
  // First edits can land under a variant-less key before the frame's
  // variant resolves (see withoutLegacyVariantlessOverride).
  const legacyKey = editing.variant?.id
    ? identity
      ? getSourceStyleOverrideKey(
          identity,
          undefined,
          context.breakpointId,
          null
        )
      : getStyleOverrideKey(
          editing.nodeId,
          undefined,
          context.breakpointId,
          null,
          editing.componentState?.id
        )
    : null;
  return (
    context.styleOverrides[key] ??
    (legacyKey ? context.styleOverrides[legacyKey] : undefined)
  );
}

function hasWholeTextFormat(
  content: TextDraftContent,
  attribute: keyof TextDraftAttributes
): boolean {
  return (
    content.kind === 'rich' &&
    content.ops.length > 0 &&
    content.ops.every((op) => op.attributes?.[attribute] === true)
  );
}

function textDraftOwnsFormat(
  editing: VCCTextEditingTarget,
  context: VccStyleDraftContext,
  attribute: keyof TextDraftAttributes
): boolean {
  if (!context.textOverrides) return false;
  const key = getTextOverrideKey({
    nodeId: editing.nodeId,
    variantId: editing.variant?.id,
    runIndex: editing.runScope?.index,
    scenarioId: editing.componentState?.id,
  });
  const stored = context.textOverrides[key];
  return Boolean(
    stored &&
    hasWholeTextFormat(stored.original, attribute) !==
      hasWholeTextFormat(stored.modified, attribute)
  );
}

function dispatchDraft(
  editing: VCCTextEditingTarget,
  context: VccStyleDraftContext,
  element: HTMLElement,
  stored: VCCStyleOverridesMap[string] | undefined,
  original: string,
  modified: string,
  identity: VCCStyleSourceIdentity | undefined
): void {
  if (!vccStyleOverrideHasChanges(original, modified) && !stored) return;
  context.dispatch(
    vccStyleOverridesUpdated({
      nodeId: editing.nodeId,
      nativeIdPath: editing.nativeIdPath,
      tagName: editing.tagName,
      textContent: element.textContent ?? '',
      original,
      modified,
      variant: editing.variant,
      ...(editing.componentState
        ? {componentState: editing.componentState}
        : {}),
      breakpointId: context.breakpointId,
      pseudoState: null,
      ...(identity ? {sourceIdentity: identity} : {}),
    })
  );
}

// Same-tick paint so the canvas and the toolbar's active state update before
// VCCStyleCascadeEffect's commit. Snapshot first — snapshot-once keeps the
// pre-draft inline values as the baseline the revert paths restore to.
// `draftIdentity` is the draft's bookkeeping identity, so strip and veto checks
// recognize this paint as the same draft's.
function paintProperty(
  element: HTMLElement,
  draftIdentity: string,
  prop: string,
  value: string | null
): void {
  snapshotVccInstanceStyleBaseline(element);
  if (value === null) {
    element.style.removeProperty(prop);
  } else {
    element.style.setProperty(prop, value);
  }
  const paintedByDraft = ensureVccInstancePaintedProps(element);
  const painted = new Set(paintedByDraft.get(draftIdentity) ?? []);
  painted.add(prop);
  paintedByDraft.set(draftIdentity, painted);
}

/**
 * Routes a bold/italic/underline toggle (mini toolbar or Cmd+B/I/U) through
 * the style panel's CSS draft when the selection is the element's entire
 * text (EXT-1854). An inline-markup wrap there is invisible to the panel —
 * the element's own CSS never changes — so the panel's typography controls
 * and the toolbar buttons disagree. Writing the same CSS draft the panel
 * writes keeps toolbar, panel, and draft bar reading one state.
 *
 * Returns false when the CSS route doesn't apply (unsupported format,
 * partial selection, or the format state comes from inline markup) — the
 * caller falls back to the execCommand wrap/unwrap path.
 */
export function maybeToggleVccTextStyleDraft(
  editing: VCCTextEditingTarget,
  format: VccTextFormat,
  context: VccStyleDraftContext
): boolean {
  const config = FORMAT_STYLES[format];
  if (!config) return false;
  const target = resolveFullTextSelection(editing);
  if (!target) return false;
  const {element, selection, range} = target;

  // Format state that comes from inline markup stays on the wrap/unwrap
  // path: an element-level CSS draft can't remove a child tag's own style.
  const node = range.commonAncestorContainer;
  for (const tag of config.tags) {
    if (getAncestorWithTag(node, element, tag) != null) return false;
  }
  const contents = range.cloneContents();
  if (contents.querySelector(config.tags.join(',').toLowerCase())) {
    return false;
  }
  // Once a markup toggle has changed this format in the text draft, keep
  // subsequent presses on that same wrap/unwrap path. Switching to an
  // element-level CSS draft after the tag was removed would show two pending
  // changes for one toggle and prevent re-applying the markup from cancelling
  // the original text change.
  if (textDraftOwnsFormat(editing, context, config.attribute)) return false;

  const win = element.ownerDocument.defaultView;
  if (!win) return false;
  const isOn = config.isComputedOn(win.getComputedStyle(element));

  const identity = resolveDraftIdentity(element, context);
  const stored = getStoredEntry(editing, context, identity);
  const original =
    stored?.original ??
    getInferredStyles(element, {
      includeInherited: true,
      backfillProperties: STYLE_PANEL_PROPERTIES,
    });

  // Turning off restores the original value when it was itself "off";
  // an original without the property drops it from the draft entirely
  // (rather than pinning the CSS default as a phantom change).
  const originalValue = cssToPropertyMap(original)[config.prop];
  const nextValue = !isOn
    ? config.onValue
    : originalValue !== undefined && !config.isValueOn(originalValue)
      ? originalValue
      : originalValue === undefined
        ? null
        : config.offValue;

  const modified = updateStyleLessProperty(
    stored?.modified ?? original,
    config.prop,
    nextValue
  );

  dispatchDraft(
    editing,
    context,
    element,
    stored,
    original,
    modified,
    identity
  );
  paintProperty(
    element,
    identity ? getSourceStyleOverrideKeyBase(identity) : editing.nodeId,
    config.prop,
    nextValue
  );

  // Re-assert the selection so the toolbar's selectionchange listener
  // recomputes the button's active state — execCommand does this
  // implicitly on the wrap path.
  selection.removeAllRanges();
  selection.addRange(range);

  return true;
}

/**
 * Companion to the CSS route for Clear Formatting: `removeFormat` only
 * strips markup, so a bold/italic/underline draft written by
 * `maybeToggleVccTextStyleDraft` would survive it. Resets those properties
 * back to their original values in the active draft. Callers still run the
 * markup clear — both can apply to the same selection.
 */
export function clearVccTextStyleDrafts(
  editing: VCCTextEditingTarget,
  context: VccStyleDraftContext
): void {
  const target = resolveFullTextSelection(editing);
  if (!target) return;
  const {element} = target;
  const identity = resolveDraftIdentity(element, context);
  const stored = getStoredEntry(editing, context, identity);
  if (!stored) return;

  const originalMap = cssToPropertyMap(stored.original);
  const modifiedMap = cssToPropertyMap(stored.modified);
  let modified = stored.modified;
  const repainted: Array<[string, string | null]> = [];
  for (const config of Object.values(FORMAT_STYLES)) {
    if (!config) continue;
    const originalValue = originalMap[config.prop] ?? null;
    if ((modifiedMap[config.prop] ?? null) === originalValue) continue;
    modified = updateStyleLessProperty(modified, config.prop, originalValue);
    repainted.push([config.prop, originalValue]);
  }
  if (repainted.length === 0) return;

  dispatchDraft(
    editing,
    context,
    element,
    stored,
    stored.original,
    modified,
    identity
  );
  const draftIdentity = identity
    ? getSourceStyleOverrideKeyBase(identity)
    : editing.nodeId;
  for (const [prop, value] of repainted) {
    paintProperty(element, draftIdentity, prop, value);
  }
}
